import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Edit2, Trash2, Search, Layers, ChevronDown, ChevronUp } from "lucide-react";
import Wrapper from "../../../components/Wrapper";
import { hardwareApi } from "../../../services/labApi";
import { formatHardwareLabel } from "./hardwareUtils";

export default function HardwarePage() {
  const [hardwareList, setHardwareList] = useState([]);
  const [components, setComponents] = useState([]);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("table");
  
  const [showModal, setShowModal] = useState(false);
  const [editingHardware, setEditingHardware] = useState(null);
  const [expandedBoms, setExpandedBoms] = useState(new Set());
  
  const [formData, setFormData] = useState({
    name: "",
    version_tag: "",
    current_stock: "",
    min_quantity: "",
  });
  
  const [bomForms, setBomForms] = useState({});
  const [bomSearch, setBomSearch] = useState({});

  const load = useCallback(async () => {
    const [hwRows, compRows] = await Promise.all([
        hardwareApi.getHardware(), 
        hardwareApi.getComponents()
    ]);
    setHardwareList(hwRows);
    setComponents(compRows);
  }, []);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const filteredHardware = useMemo(() => {
    const term = search.toLowerCase();
    const filtered = hardwareList.filter((hw) =>
      hw.name.toLowerCase().includes(term)
      || (hw.version_tag || '').toLowerCase().includes(term)
    );

    return filtered.sort((a, b) => {
      const aLowStock = Number(a.current_stock) <= Number(a.min_quantity || 0);
      const bLowStock = Number(b.current_stock) <= Number(b.min_quantity || 0);

      if (aLowStock && !bLowStock) return -1;
      if (!aLowStock && bLowStock) return 1;
      
      return a.name.localeCompare(b.name);
    });
  }, [hardwareList, search]);

  const openModal = (hardware) => {
    setEditingHardware(hardware || null);
    setFormData(
      hardware
        ? {
            name: hardware.name || "",
            version_tag: hardware.version_tag || "",
            current_stock: String(hardware.current_stock || ""),
            min_quantity: String(hardware.min_quantity || ""),
          }
        : { name: "", version_tag: "", current_stock: "", min_quantity: "" }
    );
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      version_tag: formData.version_tag.trim() || null,
      current_stock: Number(formData.current_stock || 0),
      min_quantity: Number(formData.min_quantity || 0),
    };
    
    if (editingHardware) {
      await hardwareApi.updateHardware(editingHardware.id, payload);
    } else {
      await hardwareApi.createHardware(payload);
    }
    
    setShowModal(false);
    await load();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this hardware?")) {
      await hardwareApi.deleteHardware(id);
      await load();
    }
  };

  const toggleBom = (id) => {
    setExpandedBoms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleBomFormChange = (hwId, field, value) => {
    setBomForms((prev) => ({
      ...prev,
      [hwId]: {
        ...(prev[hwId] || { component: "", quantity: "" }),
        [field]: value
      }
    }));
  };

  const handleBomSearchChange = (hwId, value) => {
    setBomSearch((prev) => ({ ...prev, [hwId]: value }));
  };

  const handleAddBOM = async (e, hwId) => {
    e.preventDefault();
    const form = bomForms[hwId] || {};
    if (!form.component || !form.quantity) return;
    
    try {
      await hardwareApi.addBOM(hwId, {
        component_id: form.component,
        quantity_required: Number(form.quantity || 0),
      });
      await load();
      setBomForms((prev) => ({ ...prev, [hwId]: { component: "", quantity: "" } }));
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to add component. It might already be in the BOM.");
    }
  };

  const handleRemoveBOM = async (hwId, componentId) => {
    await hardwareApi.removeBOM(hwId, componentId);
    await load();
  };

  const renderBomSection = (hw) => {
    const form = bomForms[hw.id] || { component: "", quantity: "" };
    const searchTerm = (bomSearch[hw.id] || "").toLowerCase();
    const bomItems = hw.bom || [];
    const filteredBom = searchTerm
      ? bomItems.filter((comp) => comp.name.toLowerCase().includes(searchTerm))
      : bomItems;
    
    return (
      <div className="bg-slate-50 p-4 border-t border-slate-200">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Bill of Materials</h4>
        {bomItems.length > 0 ? (
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              value={bomSearch[hw.id] || ""}
              onChange={(e) => handleBomSearchChange(hw.id, e.target.value)}
              placeholder="Search components..."
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            />
          </div>
        ) : null}
        <div className="space-y-2 mb-3">
          {bomItems.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No components added to this BOM yet.</p>
          ) : filteredBom.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No components match your search.</p>
          ) : (
            filteredBom.map((comp) => (
              <div key={`${comp.component_id}`} className="flex flex-col sm:flex-row sm:items-center justify-between bg-white border border-slate-200 rounded-lg p-3 gap-2">
                <div className="text-sm text-slate-700">
                  <span className="font-medium">{comp.name}</span>
                  <span className="text-slate-500"> — {comp.quantity_required} {comp.unit} required</span>
                </div>
                <button onClick={() => handleRemoveBOM(hw.id, comp.component_id)} className="text-red-600 hover:bg-red-50 px-2 py-1 rounded text-sm font-medium transition-colors">
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
        
        <form onSubmit={(e) => handleAddBOM(e, hw.id)} className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-200">
          <select 
            value={form.component} 
            onChange={(e) => handleBomFormChange(hw.id, "component", e.target.value)} 
            className="flex-1 px-3 py-2 border rounded-lg text-sm bg-white" 
            required
          >
            <option value="">Select component</option>
            {components.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.unit})</option>
            ))}
          </select>
          <input 
            value={form.quantity} 
            onChange={(e) => handleBomFormChange(hw.id, "quantity", e.target.value)} 
            type="number" 
            min="1" 
            placeholder="Qty" 
            className="w-full sm:w-24 px-3 py-2 border rounded-lg text-sm bg-white" 
            required 
          />
          <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-sm transition-colors">
            Add
          </button>
        </form>
      </div>
    );
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="w-full text-left">
            <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Hardware Devices</h2>
            <p className="mt-1 text-slate-500 text-left">Manage hardware units and Bill of Materials (BOM)</p>
          </div>
          <button onClick={() => openModal()} className="flex items-center gap-2 self-start bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700">
            <Plus size={18} />
            Add Hardware
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search hardware..." className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg" />
          </div>
          <div className="inline-flex gap-2">
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`px-3 py-2 rounded-lg text-sm ${viewMode === "table" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              className={`px-3 py-2 rounded-lg text-sm ${viewMode === "cards" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
            >
              Cards
            </button>
          </div>
        </div>

        {viewMode === "table" ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">Name</th>
                  <th className="px-4 py-3 text-left text-sm">Version</th>
                  <th className="px-4 py-3 text-right text-sm">Current Stock</th>
                  <th className="px-4 py-3 text-right text-sm">Min Alert</th>
                  <th className="px-4 py-3 text-right text-sm">Bill of Materials</th>
                  <th className="px-4 py-3 text-right text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredHardware.map((hw) => (
                  <React.Fragment key={hw.id}>
                    <tr className={expandedBoms.has(hw.id) ? "bg-slate-50/50" : ""}>
                      <td className="px-4 py-3 font-medium">{hw.name}</td>
                      <td className="px-4 py-3 text-slate-600">{hw.version_tag || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${hw.current_stock <= hw.min_quantity ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                          {hw.current_stock}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">{hw.min_quantity ?? 0}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => toggleBom(hw.id)} className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm inline-flex items-center gap-1.5 transition-colors">
                          <Layers size={14} />
                          {(hw.bom || []).length} items
                          {expandedBoms.has(hw.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openModal(hw)} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit Hardware"><Edit2 size={16} /></button>
                          <button onClick={() => handleDelete(hw.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Delete Hardware"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                    {expandedBoms.has(hw.id) && (
                      <tr>
                        <td colSpan="6" className="p-0">
                          {renderBomSection(hw)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredHardware.map((hw) => (
              <div key={hw.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-800">{formatHardwareLabel(hw)}</h3>
                    </div>
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openModal(hw)} className="p-2 hover:bg-slate-100 rounded-lg"><Edit2 size={16} /></button>
                      <button onClick={() => handleDelete(hw.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 mt-3">
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-semibold">Stock</p>
                      <p className={`text-lg font-bold ${hw.current_stock <= hw.min_quantity ? 'text-red-600' : 'text-slate-700'}`}>
                        {hw.current_stock}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-semibold">Min Qty</p>
                      <p className="text-lg font-medium text-slate-700">{hw.min_quantity}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-auto border-t border-slate-100">
                  <button
                    onClick={() => toggleBom(hw.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors ${expandedBoms.has(hw.id) ? 'bg-slate-50 text-emerald-700' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  >
                    <span className="flex items-center gap-2"><Layers size={16} className={expandedBoms.has(hw.id) ? "text-emerald-600" : "text-slate-400"}/> Bill of Materials</span>
                    <div className="flex items-center gap-2">
                      <span className="bg-white border border-slate-200 px-2 py-0.5 rounded text-xs text-slate-600">{(hw.bom || []).length} items</span>
                      {expandedBoms.has(hw.id) ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </div>
                  </button>
                  {expandedBoms.has(hw.id) && renderBomSection(hw)}
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md p-4">
              <h3 className="text-lg font-semibold mb-3">{editingHardware ? "Edit Hardware" : "Add Hardware"}</h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Hardware Name" className="w-full px-3 py-2 border rounded-lg" required />
                <input
                  value={formData.version_tag}
                  onChange={(e) => setFormData({ ...formData, version_tag: e.target.value })}
                  placeholder="Version (e.g. v1.0)"
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input value={formData.current_stock} onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })} type="number" placeholder="Current Stock" className="w-full px-3 py-2 border rounded-lg" />
                  <input value={formData.min_quantity} onChange={(e) => setFormData({ ...formData, min_quantity: e.target.value })} type="number" placeholder="Min Stock Alert" className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}